// frontend/src/components/staff/AutoClockOutSettings.js
//
// Workforce — TIME-007 §3 Auto-Clock-Out admin config section.
//
// Renders as a section inside StudioSettingsPage's tab-set per Zuri spec
// §3.1. Loads + saves per-StudioLocation `autoClockOutConfig` via Marcus
// tech doc §5.2 captured-curl JSON. Threshold input is in HOURS at the UI
// layer (Zuri §3.2.1) and converted to/from `thresholdMinutes` at the
// backend boundary (Marcus §5.2 normalized to minutes).
//
// Single-location v1 posture per Zuri spec §3.6: most studios run a single
// active StudioLocation; the form acts as if editing the singleton's config.
// If multiple locations land in scope, the form gains a location selector
// at the top (deferred to v1.1).
//
// Form state machine (Zuri §3.4):
//   Idle    : both buttons enabled if dirty; disabled if matches server
//   In-flight: Save shows spinner "Saving..."; Cancel disabled
//   Success : toast at top; both buttons return to disabled
//   Error   : Save returns to "Save"; banner with --status-violation accent
//
// V1.1 mount-time hydration state machine (Zuri TIME-007-V1.1-design.md §3):
//   State A — Skeleton    : GET in flight; field-shaped skeletons; Save disabled
//   State B — HydratedSaved: GET 200 + sub-doc; form populated from server
//   State C — HydratedDefaults: GET 200 + null; form populated from defaults
//                              with helper line above ("No policy saved yet…")
//   State D — Error       : GET 5xx / network / >5s timeout; error banner +
//                           Retry link + form populated from defaults
//
// Backend contract (Marcus tech doc time-007-v1.1-tech.md §3 + §4.1):
//   GET /api/workforce/studio-locations/:id/auto-clock-out-config
//     → 200 + { thresholdMinutes, behavior, enabled }  (Scenario 1)
//     → 200 + null                                     (Scenario 2)
//     → 403 admin_required                             (Scenario 3)
//     → 404 studio_location_not_found
//     → 500 server_error                               (Scenario 6)
//   PUT /api/workforce/studio-locations/:id/auto-clock-out-config
//     body { thresholdMinutes ∈ [60,1440], behavior ∈ {auto_close, flag_only},
//            enabled boolean }
//     → { studioLocation: {...} } (200)

import React, { useEffect, useMemo, useState } from "react";
import { autoClockOutAPI, payPeriodAPI } from "../../services/api";
import "./AutoClockOutSettings.css";

const MIN_HOURS = 1;
const MAX_HOURS = 24;
const MIN_MINUTES = 60;
const MAX_MINUTES = 1440;

// V1.1 first-ever-load fallback per Marcus tech doc §4.3 + Zuri §3.3.
// The server does NOT announce defaults — they live FE-side. Final values
// are Zuri's call (TIME-007-V1.1-design.md §3.3): threshold 720 (12h),
// behavior 'flag_only' (safer first-time posture), enabled false (master
// kill-switch off until admin opts in).
const AUTO_CLOCK_OUT_DEFAULTS = {
  thresholdMinutes: 720,
  behavior: "flag_only",
  enabled: false,
};

// Cancel-from-clean-slate baseline. Kept distinct from AUTO_CLOCK_OUT_DEFAULTS
// for blast-radius clarity even though the values currently coincide.
const DEFAULT_CONFIG = AUTO_CLOCK_OUT_DEFAULTS;

// 5s GET timeout per Zuri §3.1 — transition to State D rather than leave
// the admin staring at skeletons indefinitely.
const GET_CONFIG_TIMEOUT_MS = 5000;

const minutesToHours = (m) => {
  if (typeof m !== "number" || Number.isNaN(m)) return MIN_HOURS;
  return Math.round(m / 60);
};

const hoursToMinutes = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return MIN_MINUTES;
  return Math.max(MIN_MINUTES, Math.min(MAX_MINUTES, Math.round(n) * 60));
};

const draftFromConfig = (cfg) => ({
  thresholdHours: minutesToHours(cfg.thresholdMinutes),
  behavior: cfg.behavior || "auto_close",
  enabled: cfg.enabled !== false,
});

const mapServerError = (err) => {
  const data = err?.response?.data || {};
  const code = data.error || "server_error";
  switch (code) {
    case "threshold_too_low":
      return "Threshold must be at least 1 hour.";
    case "threshold_exceeds_hard_cap":
      return "Threshold cannot exceed 24 hours.";
    case "threshold_not_integer":
      return "Threshold must be a whole number of hours.";
    case "invalid_behavior":
      return "Behavior must be 'auto_close' or 'flag_only'.";
    case "invalid_enabled":
      return "Enabled must be true or false.";
    case "studio_location_not_found":
      return "Studio location not found. Refresh and try again.";
    case "admin_required":
      return "You don't have permission to change this setting.";
    default:
      return "Couldn't save settings. Please try again. If the problem persists, contact support.";
  }
};

const AutoClockOutSettings = () => {
  // Studio location — for single-tenant v1 we pull the single active location.
  const [studioLocation, setStudioLocation] = useState(null);
  const [locationLoadError, setLocationLoadError] = useState(false);

  // V1.1 mount-time hydration state. Drives Zuri §3 four-state machine:
  //   'skeleton'  → State A (GET in flight or location-resolve pending)
  //   'saved'     → State B (GET 200 + sub-doc — hydrated from server)
  //   'defaults'  → State C (GET 200 + null — first-ever-load helper line)
  //   'error'     → State D (GET 5xx / network / timeout — error banner)
  const [hydrationState, setHydrationState] = useState("skeleton");

  // Server-state snapshot (the "saved" baseline used for dirty detection).
  // null in States A, C, D — only populated in State B (hydrated from server).
  const [serverConfig, setServerConfig] = useState(null);

  // Editable draft. Initialized to defaults; replaced by server values on
  // hydration. Skeleton state hides the form values regardless.
  const [draft, setDraft] = useState(draftFromConfig(AUTO_CLOCK_OUT_DEFAULTS));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [successToast, setSuccessToast] = useState(null);
  const [thresholdFieldError, setThresholdFieldError] = useState(null);

  // Counter that bumps on Retry — re-runs the hydration effect without
  // re-resolving the studio location. Effect deps include this so that
  // a Retry click re-enters State A (skeleton) and re-issues the GET.
  const [retryCounter, setRetryCounter] = useState(0);

  // Step 1: resolve the active StudioLocation._id via the existing admin-RBAC
  // PayPeriod read. `payPeriodAPI.getCurrent()` is admin-only and idempotent
  // — its response shape (PAY-001 tech doc §4.2) ALWAYS includes
  // `payPeriod.studioLocationId` (synthesized "Open" when no period exists,
  // persisted row otherwise). Single-tenant prod resolves to the singleton
  // StudioLocation. We use this side-effect read because TIME-007 v1 does
  // not ship a dedicated `GET /studio-locations` (see Marcus tech doc).
  //
  // BUG-PROD-AUTO-CLOCKOUT-001 regression target: previously this effect
  // probed `staffAPI.getStudioLocations` which never existed; `studioLocation`
  // stayed null, blocking handleSave unconditionally. Fix landed PR #74.
  useEffect(() => {
    let cancelled = false;
    const resolveLocation = async () => {
      try {
        const res = await payPeriodAPI.getCurrent();
        if (cancelled) return;
        const locationId = res?.data?.payPeriod?.studioLocationId;
        if (locationId) {
          setStudioLocation({ _id: locationId });
          setLocationLoadError(false);
        } else {
          setLocationLoadError(true);
        }
      } catch (e) {
        if (!cancelled) {
          setLocationLoadError(true);
        }
      }
    };
    resolveLocation();
    return () => {
      cancelled = true;
    };
  }, []);

  // Step 2: GET the persisted config once the studio location is resolved.
  // TIME-007-V1.1 hydration guard per Marcus tech doc §4.2 — `cancelled`
  // cleanup prevents setState-after-unmount; 5s timeout per Zuri §3.1
  // transitions to State D rather than leaving the admin in skeleton state.
  //
  // Sequenced AFTER the location-resolve effect via the `studioLocation._id`
  // dep — the GET cannot fire before payPeriodAPI.getCurrent() settles
  // (returns the studioLocationId) per the BUG-PROD-AUTO-CLOCKOUT-001 fix.
  useEffect(() => {
    if (!studioLocation?._id) return undefined;
    let cancelled = false;
    setHydrationState("skeleton");

    // 5s soft timeout — flips to error state if GET hasn't resolved.
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      cancelled = true; // suppress the late .then/.catch handlers
      setServerConfig(null);
      setDraft(draftFromConfig(AUTO_CLOCK_OUT_DEFAULTS));
      setHydrationState("error");
    }, GET_CONFIG_TIMEOUT_MS);

    autoClockOutAPI
      .getConfig(studioLocation._id)
      .then((res) => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        const config = res?.data;
        if (config) {
          // State B — hydrated from saved sub-doc. TS narrowing pattern per
          // Marcus tech doc §4.1: truthy branch = AutoClockOutConfig.
          setServerConfig(config);
          setDraft(draftFromConfig(config));
          setHydrationState("saved");
        } else {
          // State C — first-ever-load. Bare `null` body per §1 contract;
          // FE owns defaults per §4.3.
          setServerConfig(null);
          setDraft(draftFromConfig(AUTO_CLOCK_OUT_DEFAULTS));
          setHydrationState("defaults");
        }
      })
      .catch(() => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        // State D — recoverable error. Per Zuri §3.4 + AC Scenario 6: form
        // falls back to defaults; Save remains enabled (admin can still
        // commit a known-good policy through the transient error).
        setServerConfig(null);
        setDraft(draftFromConfig(AUTO_CLOCK_OUT_DEFAULTS));
        setHydrationState("error");
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [studioLocation?._id, retryCounter]);

  const isSkeleton = hydrationState === "skeleton";
  const isError = hydrationState === "error";
  const isDefaults = hydrationState === "defaults";

  const dirty = useMemo(() => {
    if (!serverConfig) return true; // first-time-config — Save enabled per §3.7
    return (
      draft.thresholdHours !== minutesToHours(serverConfig.thresholdMinutes) ||
      draft.behavior !== (serverConfig.behavior || "auto_close") ||
      draft.enabled !== (serverConfig.enabled !== false)
    );
  }, [draft, serverConfig]);

  const validateThreshold = (h) => {
    if (h === "" || h === null || h === undefined) {
      return "Threshold is required.";
    }
    const n = Number(h);
    if (!Number.isFinite(n)) return "Threshold must be a number.";
    if (!Number.isInteger(n)) return "Threshold must be a whole number of hours.";
    if (n < MIN_HOURS) return `Threshold must be at least ${MIN_HOURS} hour.`;
    if (n > MAX_HOURS) return `Threshold cannot exceed ${MAX_HOURS} hours.`;
    return null;
  };

  const handleThresholdChange = (raw) => {
    const error = validateThreshold(raw);
    setThresholdFieldError(error);
    setDraft((d) => ({ ...d, thresholdHours: raw }));
  };

  const handleSave = async () => {
    if (!studioLocation?._id) {
      setSaveError(
        "Couldn't determine the active studio location. Refresh and try again."
      );
      return;
    }
    const errMsg = validateThreshold(draft.thresholdHours);
    if (errMsg) {
      setThresholdFieldError(errMsg);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setThresholdFieldError(null);
    try {
      const payload = {
        thresholdMinutes: hoursToMinutes(draft.thresholdHours),
        behavior: draft.behavior,
        enabled: Boolean(draft.enabled),
      };
      const res = await autoClockOutAPI.setConfig(studioLocation._id, payload);
      const updated = res?.data?.studioLocation;
      if (updated) {
        setStudioLocation(updated);
        const cfg = updated.autoClockOutConfig || payload;
        setServerConfig(cfg);
        setDraft(draftFromConfig(cfg));
      } else {
        // Server contract mismatch — fall back to optimistic apply of draft.
        setServerConfig(payload);
      }
      // After a successful save, regardless of the mount-time state we
      // came from (defaults, error), we now have a saved config — flip
      // to State B so the helper / banner copy stops showing.
      setHydrationState("saved");
      setSuccessToast({
        kind: "success",
        message: "Auto-Clock-Out settings saved",
      });
      setTimeout(() => setSuccessToast(null), 4000);
    } catch (e) {
      setSaveError(mapServerError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!serverConfig) {
      setDraft(draftFromConfig(DEFAULT_CONFIG));
    } else {
      setDraft(draftFromConfig(serverConfig));
    }
    setSaveError(null);
    setThresholdFieldError(null);
  };

  const handleRetry = () => {
    setRetryCounter((c) => c + 1);
  };

  // Hard load-error path — the studio location itself failed to resolve
  // (no payPeriod.studioLocationId returned). Without an _id we cannot
  // call GET /auto-clock-out-config; render the legacy load-error banner
  // and let the admin reload the page. Distinct from Zuri §3.4 State D
  // error which is a recoverable GET failure where we DO have an _id.
  if (locationLoadError) {
    return (
      <div className="auto-clock-out-section" data-testid="auto-clock-out-section">
        <h2>Auto-Clock-Out</h2>
        <p className="acos-intro">
          <em>
            Automatically close any shift that&apos;s been open longer than the
            threshold below. The closed shift is flagged for your review on the
            timesheet review screen — staff hours are never silently approved
            without your confirmation.
          </em>
        </p>
        <div className="acos-banner acos-banner-error" role="alert">
          Couldn&apos;t load Auto-Clock-Out settings.{" "}
          <button
            type="button"
            className="acos-link"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // State A — Skeleton (Zuri §3.1). Section title + field labels remain
  // visible; only field VALUES + buttons render as skeletons. Save is
  // disabled. The whole section wrapper carries aria-busy + aria-label.
  if (isSkeleton) {
    return (
      <div
        className="auto-clock-out-section"
        data-testid="auto-clock-out-section"
        aria-busy="true"
        aria-label="Loading Auto-Clock-Out settings"
      >
        <h2>Auto-Clock-Out</h2>
        <p className="acos-intro">
          <em>
            Automatically close any shift that&apos;s been open longer than the
            threshold below. The closed shift is flagged for your review on the
            timesheet review screen — staff hours are never silently approved
            without your confirmation.
          </em>
        </p>
        <div className="acos-form" data-testid="acos-skeleton">
          <div className="acos-field">
            <span className="acos-label">Threshold (hours)</span>
            <span
              className="acos-skeleton acos-skeleton-input acos-skeleton-narrow"
              aria-hidden="true"
            />
          </div>
          <div className="acos-field">
            <span className="acos-label">
              Behavior when threshold is exceeded
            </span>
            <span
              className="acos-skeleton acos-skeleton-input"
              aria-hidden="true"
            />
            <span
              className="acos-skeleton acos-skeleton-input"
              aria-hidden="true"
            />
          </div>
          <div className="acos-field">
            <span className="acos-label">Enabled</span>
            <span
              className="acos-skeleton acos-skeleton-toggle"
              aria-hidden="true"
            />
          </div>
          <div className="acos-footer">
            <span
              className="acos-skeleton acos-skeleton-button"
              aria-hidden="true"
            />
            <span
              className="acos-skeleton acos-skeleton-button"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auto-clock-out-section" data-testid="auto-clock-out-section">
      <h2>Auto-Clock-Out</h2>
      <p className="acos-intro">
        <em>
          Automatically close any shift that&apos;s been open longer than the
          threshold below. The closed shift is flagged for your review on the
          timesheet review screen — staff hours are never silently approved
          without your confirmation.
        </em>
      </p>

      {/* State C — Hydrated, no saved config. Helper line above the form. */}
      {isDefaults && (
        <div
          className="acos-banner acos-banner-info"
          role="status"
          data-testid="acos-defaults-helper"
        >
          <strong>No Auto-Clock-Out policy saved yet.</strong> The values shown
          below are the system defaults. Adjust and click <strong>Save</strong>{" "}
          to make them your active policy.
        </div>
      )}

      {/* State D — Recoverable GET error. Banner + Retry above the form. */}
      {isError && (
        <div
          className="acos-banner acos-banner-error"
          role="alert"
          data-testid="acos-load-error-banner"
        >
          <strong>Couldn&apos;t load Auto-Clock-Out settings.</strong> Showing
          system defaults — your saved policy may be different.{" "}
          <button
            type="button"
            className="acos-link"
            onClick={handleRetry}
            data-testid="acos-retry"
          >
            Retry
          </button>
        </div>
      )}

      {saveError && (
        <div className="acos-banner acos-banner-error" role="alert">
          {saveError}
        </div>
      )}

      <div className="acos-form">
        <div className="acos-field">
          <label className="acos-label" htmlFor="acos-threshold">
            Threshold (hours)
          </label>
          <input
            id="acos-threshold"
            type="number"
            min={MIN_HOURS}
            max={MAX_HOURS}
            step={1}
            value={draft.thresholdHours}
            onChange={(e) => handleThresholdChange(e.target.value)}
            className="acos-input acos-input-narrow"
            data-testid="acos-threshold"
            aria-describedby="acos-threshold-help"
          />
          <p id="acos-threshold-help" className="acos-helper">
            Shifts open longer than this are auto-closed at the threshold
            mark — not at the time the engine runs. (e.g., a 13-hour shift
            gets <code>punchOutTime = punchInTime + 12h</code>, not{" "}
            <code>now()</code>.)
          </p>
          {thresholdFieldError && (
            <p className="acos-field-error" role="alert">
              {thresholdFieldError}
            </p>
          )}
        </div>

        <div className="acos-field">
          <span className="acos-label">
            Behavior when threshold is exceeded
          </span>
          <label className="acos-radio-label">
            <input
              type="radio"
              name="acos-behavior"
              value="auto_close"
              checked={draft.behavior === "auto_close"}
              onChange={() =>
                setDraft((d) => ({ ...d, behavior: "auto_close" }))
              }
              data-testid="acos-behavior-auto-close"
            />
            <span>
              <strong>auto_close</strong> (default) — Close the shift
              automatically and flag for review on the timesheet review
              screen.
            </span>
          </label>
          <label className="acos-radio-label">
            <input
              type="radio"
              name="acos-behavior"
              value="flag_only"
              checked={draft.behavior === "flag_only"}
              onChange={() =>
                setDraft((d) => ({ ...d, behavior: "flag_only" }))
              }
              data-testid="acos-behavior-flag-only"
            />
            <span>
              <strong>flag_only</strong> — Leave the shift open and alert
              me on the daily-review dashboard. I&apos;ll close it manually
              after investigating.
            </span>
          </label>
          <p className="acos-helper">
            <code>auto_close</code> is the recommended default for
            compliance — it bounds payroll-eligible hours at the threshold.
            Use <code>flag_only</code> if you want to keep the data raw and
            decide per-case.
          </p>
        </div>

        <div className="acos-field">
          <label className="acos-checkbox-label">
            <input
              type="checkbox"
              checked={Boolean(draft.enabled)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, enabled: e.target.checked }))
              }
              data-testid="acos-enabled"
            />
            <span>
              <strong>Enabled</strong> — when off, the engine ignores this
              studio location entirely (master kill-switch).
            </span>
          </label>
        </div>

        <div className="acos-deferred">
          <h3 className="acos-deferred-heading">
            <em>Per-staff overrides &mdash; coming in v1.1</em>
          </h3>
          <p className="acos-deferred-body">
            Most studios run a single threshold for all staff. If you have
            inventory days or special-event shifts that legitimately exceed
            12 hours, per-staff overrides will let you set exceptions in a
            future release.
          </p>
        </div>

        <div className="acos-footer">
          <button
            type="button"
            className="acos-btn acos-btn-secondary"
            onClick={handleCancel}
            disabled={saving || !dirty}
            data-testid="acos-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="acos-btn acos-btn-primary"
            onClick={handleSave}
            disabled={saving || !dirty || Boolean(thresholdFieldError)}
            data-testid="acos-save"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {successToast && (
        <div
          className="acos-toast acos-toast-success"
          role="status"
          onClick={() => setSuccessToast(null)}
        >
          {successToast.message}
        </div>
      )}
    </div>
  );
};

export default AutoClockOutSettings;
